import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';
import { ApiError, authClient } from '@/shared/api';
import { paths } from '@/shared/lib/paths';
import { Form } from '@/shared/ui/form';
import { applyFieldErrors } from '@/shared/ui/form/apply-field-errors';
import { Button } from '@/shared/ui/primitives/button';
import { authErrorMessage } from '../components/auth-errors';
import {
  type ResetPasswordFormValues,
  resetPasswordFormSchema,
} from '../components/auth-form-schemas';
import { AuthPageLayout } from '../components/auth-page-layout';
import { FormAlert } from '../components/form-alert';
import { TextField } from '../components/text-field';
import { tokenFromHash } from './token-from-hash';

const INVALID_TOKEN = 'PASSWORD_RESET_TOKEN_INVALID';

function InvalidLink(): ReactNode {
  return (
    <AuthPageLayout title="Link inválido">
      <p role="alert">
        Este link de redefinição é inválido, expirou ou já foi usado. Peça um link novo.
      </p>
      <Link to={paths.forgotPassword()} className="text-sm underline underline-offset-4">
        Pedir um link novo
      </Link>
    </AuthPageLayout>
  );
}

/**
 * AUTH-4: define a senha nova com o token do e-mail. O token chega no fragmento da URL (não
 * vai ao servidor nem ao Referer) e sai da barra de endereço assim que é lido.
 */
export function ResetPasswordPage(): ReactNode {
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(() => tokenFromHash(location.hash));
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: { newPassword: '', confirmation: '' },
  });

  useEffect(() => {
    if (location.hash !== '') void navigate(location.pathname, { replace: true });
  }, [location.hash, location.pathname, navigate]);

  async function submit(values: ResetPasswordFormValues): Promise<void> {
    if (token === null) return;
    setFailure(null);
    try {
      await authClient.confirmPasswordReset({ token, newPassword: values.newPassword });
      setDone(true);
    } catch (error) {
      if (error instanceof ApiError && error.code === INVALID_TOKEN) {
        setInvalid(true);
        return;
      }
      if (!applyFieldErrors(error, form.setError, ['newPassword'])) {
        setFailure(authErrorMessage(error, 'Não foi possível redefinir a senha. Tente de novo.'));
      }
    }
  }

  if (token === null || invalid) return <InvalidLink />;
  if (done) {
    return (
      <AuthPageLayout title="Senha redefinida">
        <p role="status">
          Sua senha nova já vale. Por segurança, todas as sessões abertas foram encerradas.
        </p>
        <Button asChild className="w-full">
          <Link to={paths.login()}>Entrar</Link>
        </Button>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      title="Criar senha nova"
      description="Use de 12 a 128 caracteres. Uma frase longa é fácil de lembrar e difícil de adivinhar."
    >
      <Form {...form}>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            void form.handleSubmit(submit)(event);
          }}
        >
          {failure !== null && <FormAlert>{failure}</FormAlert>}
          <TextField
            control={form.control}
            name="newPassword"
            label="Senha nova"
            type="password"
            autoComplete="new-password"
          />
          <TextField
            control={form.control}
            name="confirmation"
            label="Confirme a senha nova"
            type="password"
            autoComplete="new-password"
          />
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            Salvar senha nova
          </Button>
        </form>
      </Form>
    </AuthPageLayout>
  );
}
